import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  Button,
  Tag,
  Space,
  message,
  Input,
  Select,
  Card,
  Typography,
  Tooltip,
  Popconfirm,
  Row,
  Col,
  Spin,
  AutoComplete,
} from 'antd';
import {
  EyeOutlined,
  DeleteOutlined,
  MessageOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { customizationAPI, userAPI } from '../api';
import AdminCustomizationDialog from './AdminCustomizationDialog';
import { normalizeMoney, moneyToCents } from '../utils/money';

const { Title } = Typography;
const { Option } = Select;
const { Search } = Input;

const CustomizationManagement = () => {
  const [customizations, setCustomizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [selectedCustomization, setSelectedCustomization] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });

  // 搜索和过滤参数
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dropshipperFilter, setDropshipperFilter] = useState(null);
  const [userSearchInput, setUserSearchInput] = useState('');
  const [userOptions, setUserOptions] = useState([]);
  const [userOptionMap, setUserOptionMap] = useState({});
  const [userSearchLoading, setUserSearchLoading] = useState(false);

  useEffect(() => {
    fetchCustomizations();
  }, [pagination.current, pagination.pageSize, searchText, statusFilter, dropshipperFilter]);

  const fetchCustomizations = async () => {
    setLoading(true);
    try {
      const params = {
        skip: (pagination.current - 1) * pagination.pageSize,
        limit: pagination.pageSize,
      };
      
      if (searchText) params.search = searchText;
      if (statusFilter) params.status = statusFilter;
      if (dropshipperFilter) params.dropshipper_id = dropshipperFilter;

      const response = await customizationAPI.getAll(params);
      // 按创建时间降序排序，最晚创建的排在最上面
      const sortedCustomizations = response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setCustomizations(sortedCustomizations);
      
      // Note: Backend doesn't return total count, so we estimate
      // In a real implementation, the backend should return total count
      setPagination(prev => ({
        ...prev,
        total: sortedCustomizations.length === pagination.pageSize 
          ? (pagination.current * pagination.pageSize) + 1 
          : (pagination.current - 1) * pagination.pageSize + sortedCustomizations.length
      }));
    } catch (error) {
      message.error('获取定制需求列表失败');
      
    } finally {
      setLoading(false);
    }
  };

  const handleUserSearch = async (value) => {
    const query = (value || '').trim();
    setUserSearchInput(value);
    if (!query) {
      setUserOptions([]);
      return;
    }
    setUserSearchLoading(true);
    try {
      const response = await userAPI.getAll({
        page: 1,
        page_size: 20,
        role: 'dropshipper',
        search: query,
      });
      const results = (response.data?.data || []).filter(user => user.role === 'dropshipper');
      setUserOptions(results);
      setUserOptionMap(prev => {
        const next = { ...prev };
        results.forEach(user => {
          next[user.id] = user;
        });
        return next;
      });
    } catch (error) {
      message.error('搜索用户失败');
    } finally {
      setUserSearchLoading(false);
    }
  };

  const handleDeleteCustomization = async (customizationId) => {
    try {
      await customizationAPI.delete(customizationId);
      message.success('定制需求删除成功');
      fetchCustomizations();
    } catch (error) {
      message.error('删除定制需求失败');
      
    }
  };

  const handleViewCustomization = (customization) => {
    setSelectedCustomization(customization);
    setDialogVisible(true);
  };

  const handleDialogClose = () => {
    setDialogVisible(false);
    setSelectedCustomization(null);
    fetchCustomizations(); // 刷新列表以更新状态
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleDropshipperFilter = (value) => {
    setDropshipperFilter(value || null);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const handleReset = () => {
    setSearchText('');
    setStatusFilter('');
    setDropshipperFilter(null);
    setUserSearchInput('');
    setUserOptions([]);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const formatUserLabel = (user) =>
    `${user.username}${user.email ? ` (${user.email})` : ''}`;

  const userSelectOptions = useMemo(() => {
    const base = userOptions || [];
    const filterId = dropshipperFilter ? Number(dropshipperFilter) : null;
    const extended =
      filterId && !base.some(user => user?.id === filterId)
        ? [...base, userOptionMap[filterId]].filter(Boolean)
        : base;
    return extended.map(user => ({
      value: String(user.id),
      label: formatUserLabel(user),
    }));
  }, [userOptions, userOptionMap, dropshipperFilter]);

  const columns = [
    {
      title: '定制名称',
      dataIndex: 'customization_name',
      key: 'customization_name',
      width: '20%',
      ellipsis: true,
      render: (text) => <strong>{text}</strong>,
      sorter: (a, b) => a.customization_name.localeCompare(b.customization_name),
    },
    {
      title: '客户',
      dataIndex: 'dropshipper',
      key: 'dropshipper',
      width: '15%',
      ellipsis: true,
      render: (dropshipper) => (
        <Space>
          <UserOutlined />
          <span>{dropshipper?.username || '未知'}</span>
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: '12%',
      render: (status) => (
        <Tag 
          color={status === 'approved' ? 'green' : 'orange'}
          icon={status === 'approved' ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
        >
          {status === 'approved' ? '已审核' : '待审核'}
        </Tag>
      ),
      filters: [
        { text: '已审核', value: 'approved' },
        { text: '待审核', value: 'unapproved' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: '每包裹价格',
      dataIndex: 'price_prepacket',
      key: 'price_prepacket',
      render: (price) => (price !== null && price !== undefined) ? normalizeMoney(price) : '未设置',
      sorter: (a, b) => Number(moneyToCents(a.price_prepacket ?? '0') - moneyToCents(b.price_prepacket ?? '0')),
      width: '12%',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '13%',
      render: (date) => new Date(date).toLocaleDateString(),
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    },
    {
      title: '最后活动',
      dataIndex: 'latest_comment_at',
      key: 'latest_comment_at',
      width: '13%',
      render: (date) => date ? new Date(date).toLocaleDateString() : '无活动',
      sorter: (a, b) => {
        const dateA = a.latest_comment_at ? new Date(a.latest_comment_at) : new Date(0);
        const dateB = b.latest_comment_at ? new Date(b.latest_comment_at) : new Date(0);
        return dateA - dateB;
      },
    },
    {
      title: '操作',
      key: 'action',
      width: '15%',
      render: (_, record) => (
        <Tooltip title="删除定制需求">
          <Popconfirm
            title="确定要删除这个定制需求吗？"
            description="此操作无法撤销，将删除所有对话和附件。"
            onConfirm={(e) => {
              e?.stopPropagation();
              handleDeleteCustomization(record.id);
            }}
            okText="确定"
            cancelText="取消"
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
              onClick={(e) => e.stopPropagation()}
            />
          </Popconfirm>
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Title level={2} style={{ margin: 0, marginBottom: 16 }}>定制需求管理</Title>
          
          {/* Search and Filter Controls */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <Search
                placeholder="按定制名称搜索"
                enterButton={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onSearch={handleSearch}
                allowClear
              />
            </Col>
            <Col xs={12} sm={6} md={4}>
              <Select
                placeholder="按状态筛选"
                value={statusFilter}
                onChange={handleStatusFilter}
                allowClear
                style={{ width: '100%' }}
              >
                <Option value="approved">已审核</Option>
                <Option value="unapproved">待审核</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <AutoComplete
                value={userSearchInput}
                options={userSelectOptions}
                onSelect={(value, option) => {
                  handleDropshipperFilter(value);
                  setUserSearchInput(option?.label ?? '');
                }}
                onChange={(value, option) => {
                  setUserSearchInput(value);
                  if (!value) {
                    setUserOptions([]);
                    handleDropshipperFilter(null);
                  } else if (!option) {
                    handleDropshipperFilter(null);
                  }
                }}
                notFoundContent={userSearchLoading ? <Spin size="small" /> : null}
                style={{ width: '100%' }}
                filterOption={false}
              >
                <Search
                  placeholder="筛选用户"
                  enterButton={<SearchOutlined />}
                  allowClear
                  loading={userSearchLoading}
                  onSearch={handleUserSearch}
                />
              </AutoComplete>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Space>
                <Button 
                  icon={<ReloadOutlined />}
                  onClick={fetchCustomizations}
                >
                  刷新
                </Button>
                <Button 
                  icon={<FilterOutlined />}
                  onClick={handleReset}
                >
                  重置筛选
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        <Table
          columns={columns}
          dataSource={customizations}
          loading={loading}
          rowKey="id"
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `显示第 ${range[0]}-${range[1]} 条，共 ${total} 个定制需求`,
            pageSizeOptions: ['20', '50', '100'],
          }}
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: () => handleViewCustomization(record),
            style: { cursor: 'pointer' }
          })}
          size="middle"
        />
      </Card>

      {/* Customization Dialog */}
      {dialogVisible && selectedCustomization && (
        <AdminCustomizationDialog
          customization={selectedCustomization}
          visible={dialogVisible}
          onClose={handleDialogClose}
        />
      )}
    </div>
  );
};

export default CustomizationManagement;
